/* eslint-disable no-use-before-define */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, MenuItem } from '@mui/material';
import PropTypes from 'prop-types';

import { companyService, userService, zipCodeService } from '../../../../../services/napoleon';
import { updateCompanyId, updateUserId } from '../../../../../redux/actions/userActions';
import useToastActions from '../../../../../redux/actions/useToastActions';
import { REGISTER_STEPS } from '../../../../../constants/registerSteps';
import { ButtonComponent } from '../../../../../components';
import { Input, SelectInput } from '../../../../../components/Form';
import { isValidZipCode } from '../../../../../utils';
import brazilianStates from '../../../../../utils/brazilianStates';
import styles, { FormContainer, Footer } from '../../styles';
import { errorMessages } from './helpers';
import useFormStep from './useFormStep';
import StepsHeadline from './StepsHeadline';

const formStep = REGISTER_STEPS.COMPANY_ADDRESS;
const notMandatoryFields = ['complement'];

export default function CompanyAddress({ handleBack, handleNext }) {
  const { error } = useToastActions();
  const dispatch = useDispatch();
  const { steps, companyId, authByGoogle } = useSelector((state) => state.registerStepReducer);
  const email = useSelector((state) => state.registerStepReducer.steps[REGISTER_STEPS.SIGN_IN_INFO]?.email);

  let { userId } = useSelector((state) => state.registerStepReducer);
  const companyInfo = steps[REGISTER_STEPS.COMPANY_INFO];

  const [isFetchingData, setIsFetchingData] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  async function registerCompany() {
    const payload = { ...companyInfo, ...form };

    if (!companyId) {
      try {
        const { data } = await companyService.create(payload);
        if (!userId) {
          const response = await userService.findDataUserByEmail(
            !authByGoogle ? authByGoogle.email : email,
          );

          userId = response.data.id;
          dispatch(updateUserId(userId));
        }
        await userService.update({ id: userId, companyId: data.company.id });
        dispatch(updateCompanyId(data.company.id));
        handleNext();
      } catch (err) {
        const responseError = err?.response?.data?.message?.toLowerCase();
        let toastError = 'Erro ao tentar criar empresa. Tente novamente mais tarde.';

        if (responseError?.includes('cnpj already in use')) {
          toastError = 'CNPJ já cadastrado.';
        }

        error(toastError);
      }
    } else {
      try {
        await companyService.update({ ...payload, id: companyId });
        handleNext();
      } catch (err) {
        error('Erro ao tentar atualizar empresa. Tente novamente mais tarde.');
      }
    }
  }

  const validateField = (name, value) => {
    if (notMandatoryFields.includes(name) && !value?.trim()) return '';
    if (!value?.trim()) return errorMessages.MANDATORY_FIELD;

    switch (name) {
      case 'zipCode':
        if (!isValidZipCode(value)) return 'CEP inválido';
        break;
      case 'street':
        if (value.length > 200) return 'Rua deve ter no máximo 200 caracteres';
        break;
      case 'number':
        if (value.length > 10) return 'Número deve ter no máximo 10 caracteres';
        break;
      case 'neighborhood':
        if (value.length > 200) return 'Bairro deve ter no máximo 200 caracteres';
        break;
      case 'complement':
        if (value.length > 200) return 'Complemento deve ter no máximo 200 caracteres';
        break;
      case 'city':
        if (value.length > 200) return 'Cidade deve ter no máximo 200 caracteres';
        break;
      default:
        return '';
    }
    return '';
  };

  const {
    form,
    setForm,
    formErrors,
    setFormErrors,
    isLoading,
    inputRef,
    handleChange,
    handleSubmit,
    updateRedux,
    isStepValid,
  } = useFormStep(formStep, validateField, registerCompany);

  function updateZipCodeFields(data, updateFormErrors = true) {
    setForm((prev) => ({
      ...prev,
      street: data.street,
      neighborhood: data.neighborhood,
      city: data.city,
      state: data.state,
    }));

    // When zip code is invalid it's not necessary to validate the other fields
    if (updateFormErrors) {
      setFormErrors((prev) => ({ ...prev, street: validateField('street', data.street) }));
      setFormErrors((prev) => ({
        ...prev,
        neighborhood: validateField('neighborhood', data.neighborhood),
      }));
      setFormErrors((prev) => ({ ...prev, city: validateField('city', data.city) }));
      setFormErrors((prev) => ({ ...prev, state: validateField('state', data.state) }));
    }
  }

  const searchZipCode = async (zipCode) => {
    setIsFetchingData(true);

    zipCodeService
      .getZipCode(zipCode)
      .then((response) => {
        const { data } = response;
        updateZipCodeFields(data);
      })
      .catch(() => {
        setFormErrors((prev) => ({ ...prev, zipCode: 'CEP inválido' }));
        updateZipCodeFields(
          {
            street: '',
            neighborhood: '',
            city: '',
            state: '',
          },
          false,
        );
        error('Erro ao buscar CEP.');
      })
      .finally(() => {
        setIsFetchingData(false);
        handleFocus();
      });
  };

  function shouldSeachZipCode(newValue) {
    if (isValidZipCode(newValue) && isMounted) {
      searchZipCode(newValue);
    }
  }

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    handleFocus();
    setIsMounted(true);
  }, []);

  return (
    <FormContainer onSubmit={handleSubmit}>
      <StepsHeadline />

      <FormControl sx={styles.textField} variant="standard">
        <Input
          placeholder="CEP"
          value={form.zipCode}
          ref={inputRef}
          onAccept={(value) => {
            handleChange({ target: { name: 'zipCode', value } });
            shouldSeachZipCode(value);
          }}
          type="text"
          name="zipCode"
          mask="00000-000"
          error={!!formErrors.zipCode}
          helperText={formErrors.zipCode}
          disabled={isFetchingData || isLoading}
        />
      </FormControl>

      <FormControl sx={styles.textFieldTwoInputs} variant="standard">
        <Input
          placeholder="Rua"
          value={form.street}
          onChange={handleChange}
          type="text"
          name="street"
          inputProps={{ maxLength: 200 }}
          error={!!formErrors.street}
          helperText={formErrors.street}
          style={styles.largeTextField}
          disabled={isFetchingData || isLoading}
        />

        <Input
          placeholder="Nº"
          value={form.number}
          onChange={handleChange}
          type="text"
          name="number"
          inputProps={{ maxLength: 10 }}
          error={!!formErrors.number}
          helperText={formErrors.number}
          style={styles.smallTextField}
          disabled={isFetchingData || isLoading}
        />
      </FormControl>

      <FormControl sx={styles.textField} variant="standard">
        <Input
          placeholder="Bairro"
          value={form.neighborhood}
          onChange={handleChange}
          type="text"
          name="neighborhood"
          inputProps={{ maxLength: 200 }}
          error={!!formErrors.neighborhood}
          helperText={formErrors.neighborhood}
          disabled={isFetchingData || isLoading}
        />
      </FormControl>

      <FormControl sx={styles.textField} variant="standard">
        <Input
          placeholder="Complemento"
          value={form.complement}
          onChange={handleChange}
          type="text"
          name="complement"
          inputProps={{ maxLength: 200 }}
          error={!!formErrors.complement}
          helperText={formErrors.complement}
          disabled={isFetchingData || isLoading}
        />
      </FormControl>

      <FormControl sx={styles.textFieldTwoInputs} variant="standard">
        <Input
          placeholder="Cidade"
          value={form.city}
          onChange={handleChange}
          type="text"
          name="city"
          inputProps={{ maxLength: 200 }}
          error={!!formErrors.city}
          helperText={formErrors.city}
          style={styles.largeTextField}
          disabled={isFetchingData || isLoading}
        />

        <SelectInput
          id="simple-select-standard"
          value={form.state}
          name="state"
          onChange={handleChange}
          placeholder="UF"
          error={!!formErrors.state}
          helperText={formErrors.state}
          sx={styles.stateField}
          disabled={isFetchingData || isLoading}
        >
          {brazilianStates.map(({ acronym }, key) => (
            <MenuItem value={acronym} key={`${acronym}-${key}`}>
              {acronym}
            </MenuItem>
          ))}
        </SelectInput>
      </FormControl>

      <Footer style={styles.registerStepsFooter}>
        <ButtonComponent
          size="small"
          variant="outlined"
          disabled={isLoading || isFetchingData}
          onClick={() => {
            updateRedux();
            handleBack();
          }}
        >
          Voltar
        </ButtonComponent>

        <ButtonComponent
          type={isLoading || isFetchingData ? 'button' : 'submit'}
          size="small"
          disabled={!isStepValid() || isLoading || isFetchingData}
          loading={isLoading || isFetchingData}
        >
          Próximo
        </ButtonComponent>
      </Footer>
    </FormContainer>
  );
}

CompanyAddress.propTypes = {
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};
